import {memo, useMemo} from 'react'
import simplify from 'simplify-js'
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryContainer,
  VictoryBrushContainer,
} from 'victory'

import {PatchedVictoryLine, PatchedVictoryArea} from '../../../../../../containers/PatchedVictory'
import cssVariables from '../../../../../../utils/cssVariables'
import {TestStatus} from '../../../Tests.types'
import {DataTypes} from '../../TestHistoryChart.types'
import {getSimplifyTolerance} from '../../TestHistoryChart.utils'

import type {PreviewProps, BrushProps} from './Preview.types'

import styles from './Preview.css'

const PREVIEW_CHART_HEIGHT = 32
const LINE_PADDING = {
  bottom: 2,
  top: 2,
}

function Nothing() {
  return null
}

const parentStyles = {
  position: 'absolute',
  left: 0,
  top: 0,
  right: 0,
  bottom: 0,
}
const lineStyles = {
  parent: parentStyles,
  data: {
    stroke: cssVariables['--ring-secondary-color'],
    strokeWidth: 1,
  },
}
const errorAreaStyles = {
  parent: parentStyles,
  data: {
    fill: cssVariables['--ring-icon-error-color'],
    fillOpacity: 0.25,
    strokeWidth: 0,
  },
}
const errorBarStyles = {
  parent: parentStyles,
  data: {
    fill: cssVariables['--ring-icon-error-color'],
    fillOpacity: 0.25,
  },
}
const brushStyles = {
  fillOpacity: 0,
}

function Brush({datum, scale}: BrushProps) {
  if (datum == null || scale == null) {
    return null
  }

  const x0 = scale.x(datum.x0)
  const x = scale.x(datum.x)
  return (
    <foreignObject y={0} height={PREVIEW_CHART_HEIGHT} x={x0} width={x - x0}>
      <div className={styles.brush} />
    </foreignObject>
  )
}

type Point = {
  x: number
  y: number
}

function Preview({maxDomain, minDomain, width, data, domain, onDomainChange}: PreviewProps) {
  const simplifiedData = simplify(data, getSimplifyTolerance(data.length - 1))
  const brushData = useMemo(
    () =>
      domain && [
        {
          x0: domain.x[0],
          x: domain.x[1],
        },
      ],
    [domain],
  )
  const failureAreas = useMemo(() => {
    const result: Point[][] = []
    let lastFailed: number | null = null
    data.forEach(item => {
      const {x} = item

      if (item.type === DataTypes.DEFAULT && item.status === TestStatus.FAILURE) {
        if (lastFailed === x - 1) {
          if (result[result.length - 1].length === 1) {
            result[result.length - 1].push({x, y: maxDomain.y})
          } else if (result[result.length - 1].length === 2) {
            result[result.length - 1][1] = {x, y: maxDomain.y}
          }
        } else {
          result.push([{x, y: maxDomain.y}])
        }

        lastFailed = x
      }
    })
    return result
  }, [data, maxDomain])
  const barData = useMemo(
    () => failureAreas.map(item => (item.length === 1 ? item[0] : null)).filter(Boolean),
    [failureAreas],
  )
  const areaData = useMemo(() => failureAreas.filter(item => item.length === 2), [failureAreas])
  return (
    <div className={styles.previewContainer}>
      <div className={styles.preview} style={{height: PREVIEW_CHART_HEIGHT}}>
        <VictoryChart
          padding={0}
          width={width}
          height={PREVIEW_CHART_HEIGHT}
          maxDomain={maxDomain}
          minDomain={minDomain}
          containerComponent={<VictoryContainer title="Test history chart preview" />}
        >
          <VictoryAxis groupComponent={<Nothing />} />
          <VictoryBar data={brushData} dataComponent={<Brush />} />
          <VictoryBar
            padding={0}
            width={width}
            height={PREVIEW_CHART_HEIGHT}
            maxDomain={maxDomain}
            minDomain={minDomain}
            style={errorBarStyles}
            data={barData}
            barWidth={1}
          />
          {areaData.map(area => (
            <PatchedVictoryArea
              key={area[0].x}
              padding={0}
              width={width}
              height={PREVIEW_CHART_HEIGHT}
              maxDomain={maxDomain}
              minDomain={minDomain}
              style={errorAreaStyles}
              data={area}
            />
          ))}
        </VictoryChart>
        <PatchedVictoryLine
          padding={LINE_PADDING}
          width={width}
          height={PREVIEW_CHART_HEIGHT}
          maxDomain={maxDomain}
          minDomain={minDomain}
          style={lineStyles}
          data={simplifiedData}
          containerComponent={
            <VictoryBrushContainer
              title="Test history chart preview"
              className={styles.victoryContainer}
              brushDimension="x"
              defaultBrushArea="move"
              brushDomain={domain}
              onBrushDomainChange={onDomainChange}
              brushStyle={brushStyles}
            />
          }
        />
      </div>
    </div>
  )
}

export default memo<PreviewProps>(Preview)
